import { ContainerTO } from "./types/response/container/Container"
import { ParameterTO } from "./types/response/parameter/Parameter"
import { Value } from "./types/response/parameter/Value"
import { GuiTO } from "./types/response/tree/Gui"

/**
 * Recursivly walk all parameters of a container.
 * @param {ContainerTO} container the root container
 * @param fnFilter the parameter filter
 * @param fnMap the parameter mapping function
 * @returns list of filtered and mapped parameters
 */
export function walkParameters<T>(
    container: ContainerTO,
    fnFilter: (p: ParameterTO) => boolean,
    fnMap: (p: ParameterTO) => T
): T[] {
    const params = container.parameters.filter(fnFilter).map(fnMap)
    return container.children
        .map((child) => walkParameters(child, fnFilter, fnMap))
        .reduce((acc, val) => acc.concat(val), params)
}

/**
 * @returns {{count:number, done:number}} the count and done values
 */
export function percentDone(container: ContainerTO): {
    count: number
    done: number
} {
    return walkParameters(
        container,
        (p: any) => p.mandatory,
        (p: any) => (p.terminal === true ? 1 : 0)
    ).reduce(
        (acc: any, val: any) => ({
            count: (acc.count += 1),
            done: (acc.done += val)
        }),
        { count: 0, done: 0 }
    )
}

/**
 * Check, if all mandatory parameters are filled in.
 * @param {ContainerTO} container the container to be checked
 * @return true, iff all mandatory parameters are filled in
 */
export function isReady(container: ContainerTO): boolean {
    if (!container) {
        return false
    }

    const traverse: any = function (c: ContainerTO) {
        return (
            c.parameters
                .filter((p) => p.mandatory === true)
                .every((p) => p.terminal === true) &&
            c.children.every((container: ContainerTO) => traverse(container))
        )
    }

    return traverse(container)
}

/**
 * Returns the spec state for a parameter.
 * @param {ParameterTO} param the parameter
 * @return {String} the spec state, e.g. NOT_SET, SET_BY_DEFAULT, SET_BY_USER
 */
export function specState(param: ParameterTO): string {
    if (!Array.isArray(param.values)) {
        return "?"
    }

    const specStates = param.values
        .filter((v) => v.selected === true)
        .map((v) => v.selectionSource)
        .filter((v) => v !== undefined)
        .filter((e, i, a) => a.indexOf(e) === i)
    switch (specStates.length) {
        case 0:
            return "NOT_SET"
        case 1:
            return specStates[0] && specStates[0].toString()
        default:
            return "MIXED"
    }
}

/**
 * Returns the selected translated value of a parameter.
 *
 * @param {ParameterTO} param Parameter
 * @param {String} defaultValue the default value if not terminal
 * @return the selected value or default value
 */
export function selectedValue(
    param: ParameterTO,
    defaultValue?: string
): string | undefined {
    if (!param || !Array.isArray(param.values)) {
        return defaultValue
    }

    const selected = param.values.find((v) => v.selected === true)
    return selected ? selected.translatedValue : defaultValue
}

/**
 * Returns the selected raw value of a parameter.
 *
 * @param {ParameterTO} param Parameter
 * @param {String | null} defaultValue the default value if not terminal
 * @return the selected value or default value
 */
export function selectedRawValue(
    param: ParameterTO,
    defaultValue?: string | null
): string | null | undefined {
    if (!param || !Array.isArray(param.values)) {
        return defaultValue
    }

    const selected = param.values.find((v) => v.selected === true)
    return selected ? selected.value : defaultValue
}

function tryParseFloat(value: any) {
    if (value === undefined || value === null) {
        return null
    }

    const result = parseFloat(value)
    return isNaN(result) ? value : result
}

/**
 * Test, if it is a numeric value.
 * @param {ValueTO} value the value to
 * @returns {Boolean}
 */
function isNumericValue(value: any) {
    return value.valueType === "INTEGER" || value.valueType === "DOUBLE"
}

type StateParameter = {
    id: string
    path: string
    name: string
    translatedName: string
    shortText: string | null
    longText: string | null
    imageUrl: string | null
    value: string
    valueTO: Value
    minValue: number | null
    maxValue: number | null
    facets: object | null
}

type State = { [key: string]: StateParameter }

/**
 * Extract parameter information from the configuration.
 *
 * @param {GuiTO} ui the gui container
 * @param {String} [numberParameter] the optional nr parameter name for instances (default is _nr)
 * @return {{ state: {}, reverse: {}}}Array with map and reverse table
 */
export function extractParameters(
    ui: GuiTO,
    numberParameter?: string
): { state: State; reverse: { [key: string]: string } } {
    const state = {} as any
    const reverse = {} as any

    const _nr = numberParameter || "_nr"

    function container(base: any, currentContainer: any) {
        currentContainer.parameters.forEach((p: any, ix: any) => {
            const path = `${base}/${p.name || ix}`
            const valueTO =
                p.values.find((v: any) => v.selected === true) || null

            let value = null
            if (valueTO !== null) {
                value = isNumericValue(valueTO)
                    ? tryParseFloat(valueTO.value)
                    : valueTO.value
            }

            state[path] = {
                id: p.id,
                path,
                name: p.name,
                translatedName: p.translatedName,
                shortText: p.shortText || null,
                longText: p.longText || null,
                imageUrl: p.imageUrl || null,
                value,
                valueTO,
                minValue: tryParseFloat(p.unformattedOriginalMinValue),
                maxValue: tryParseFloat(p.unformattedOriginalMaxValue),
                facets: p.facets || null
            }
            reverse[p.id] = path
        })

        currentContainer.children.forEach((child: any, ix: any) => {
            const nrParameter =
                child.parameters &&
                child.parameters.find((p: any) => p.name === _nr)
            const name = selectedRawValue(nrParameter, child.name || ix)
            container(`${base}/${name}`, child)
        })
    }

    container("", ui.rootContainer)
    return { state, reverse }
}

/**
 * A state selector.
 * @param {Object} state the configuration state (see @encoway/c-services-js-client:extractParameters)
 * @param {String} rpath the regular path, e.g. "/Stecker/(\\d+)/(.*)"
 * @returns {Array} an array of items
 */
export function stateSelector(
    state: State,
    rpath: string | RegExp
): StateParameter[] {
    const result: { [key: string]: StateParameter } = {}
    Object.keys(state).forEach((key) => {
        const m = key.match(rpath) as any[]
        if (!m) {
            return
        }

        let slot = result[m[1]]
        if (!slot) {
            slot = {} as StateParameter
            result[m[1]] = slot
        }

        // @ts-ignore bad assignment
        slot[m[2]] = state[key]
    })

    return Object.values(result)
}
